import {PhonesComponent} from '../phones/phones.component';
import {GenderEnum} from '../../models/user';
import {UpdatePatientInformationRequestDTO} from '../../dtos/patient';
import {Component, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {Store} from '@ngxs/store';
import {CivilStateEnum, IPatient, IPatientInformation, PatientAction, PatientState} from '../../models/patient';
import {firstValueFrom, Observable, Subscription} from 'rxjs';
import {Loader} from '../../models/loader/loader.redux';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {cloneDeep} from 'lodash';
import {ActivatedRoute, Params, Router} from '@angular/router';
import {UtilitiesService} from '../../services/utilities-service/utilities.service';
import {MessageAlertService} from '../../services/message-alert/message-alert.service';
import {countriesData, ICountryData} from '../../config/data/countries/countries.data';
import {fadeInAnimation} from '../../animations/fade-in.animation';
import {IStateData, mexicoStatesData} from '../../config/data/states/mexico-states.data';
import {HelperValidationField} from '../../class/helper/helper-validation-field.class';
import {take} from 'rxjs/operators';
import {IPhoneNumber} from '../../interfaces/phone-number/phone-number.interface';
import {PhoneNumberTypeEnum} from '../../interfaces/phone-number/phone-number-type.enum';
import {Logger} from '../../class/helper/logger.class';
import {PLATFORM_CONFIG} from '../../config/platform.config';
import {ReactiveFormType} from '../../types/form';
import {AppTourService} from '../../services/app-tour/app-tour.service';

@Component({
  selector: 'app-consultation-information',
  templateUrl: './consultation-information.component.html',
  styleUrls: ['./consultation-information.component.scss'],
  animations: [fadeInAnimation],
})
export class ConsultationInformationComponent implements OnInit, OnDestroy {
  patient$: Observable<IPatient> = this.store.select<IPatient>(PatientState.getSelectedPatient);
  @ViewChild(PhonesComponent) phonesComponent: PhonesComponent;
  patientInfo: { showOptions: boolean, isEdit: boolean };
  helperValidationField: HelperValidationField;
  mexicoStates: IStateData[];
  countries: ICountryData[];
  patientSubscription: Subscription;
  patient: IPatient;
  params: Params;
  appointmentId: string;
  civilStateEnum = CivilStateEnum;
  genderEnum = GenderEnum;
  phone: IPhoneNumber = {
    type: PhoneNumberTypeEnum.CELLULAR,
    areaCode: '',
    phoneNumber: '',
    ext: ''
  };

  platformConfig = PLATFORM_CONFIG;
  form: FormGroup<ReactiveFormType<IPatientInformation>>;
  constructor(
    private appTourService: AppTourService,
    private activatedRoute: ActivatedRoute,
    private messageAlertService: MessageAlertService,
    private store: Store,
    public utilitiesService: UtilitiesService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.initValues().then();
    this.listeners();
  }

  /**
   * Inicializa las variables necesarias.
   */
  async initValues(): Promise<void> {
    this.params = this.utilitiesService.getAllParams(this.activatedRoute);
    this.appointmentId = (await firstValueFrom(this.activatedRoute.queryParams.pipe(take(1))))?.appointmentId;
    this.patientInfo = {
      isEdit: false,
      showOptions: true
    };
    this.helperValidationField = new HelperValidationField();
    this.mexicoStates = mexicoStatesData;
    this.countries = countriesData;
  }

  /**
   * Pone los observables en escucha de cambios.
   */
  private listeners(): void {
    this.patientSubscription = this.patient$.subscribe(async data => {
      if (!data) return;
      await firstValueFrom((this.store.dispatch(new Loader(true, 'Obteniendo la información...'))));
      await this.setPatient(data);
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    });
  }

  /**
   * Se muestra el formulario en modo modificación.
   * @param isEditable Es editable (booleano)
   */
  onSetEditionMode(isEditable: boolean): void {
    if (!this.patient?.user?.phones || this.patient?.user?.phones.length === 0) {
      this.patient.user.phones = [];
      this.patient.user.phones.push({
        type: PhoneNumberTypeEnum.CELLULAR,
        areaCode: '',
        phoneNumber: '',
        ext: ''
      });
    }
    this.patientInfo.showOptions = false;
    this.patientInfo.isEdit = isEditable;
  }

  /**
   * Sale del modo modificación.
   */
  onBackPatientInfo(): void {
    this.patientInfo.showOptions = true;
    this.patientInfo.isEdit = false;
  }

  /**
   * Guarda la información del paciente.
   */
  async onSavePatient(): Promise<void> {
    // Tour mode
    if (this.appTourService.tourMode) {
      this.patientInfo.showOptions = true;
      this.patientInfo.isEdit = false;
      return;
    }

    // Normal flow
    await firstValueFrom((this.store.dispatch(new Loader(true))));
    if (this.form.invalid || !this.phonesComponent?.isValid()) {
      this.form.markAllAsTouched();
      this.form.markAsDirty();
      await firstValueFrom((this.store.dispatch(new Loader(false))));
      this.messageAlertService.createMessage('error', '¡El formulario tiene observaciones!');
      return;
    }
    if (!this.patient) return;

    try {
      const {city, country, state, civilState, street, suburb, cp, gender, birthday, email} = this.form.value;
      this.patient.user.phones = this.phonesComponent.getRawValue();
      const data: UpdatePatientInformationRequestDTO = {
        birthday: new Date(birthday),
        email,
        civilState,
        phones: this.phonesComponent.getRawValue(),
        address: {city, country, state, street, suburb, cp: cp ? cp : undefined},
        status: this.patient.status,
        gender,
      };

      await firstValueFrom((this.store.dispatch(new PatientAction.Patch(this.params.clinicId, this.patient._id, data))));
      setTimeout(() => {
        this.messageAlertService.createMessage('success', 'Datos actualizados correctamente');
        this.patientInfo.showOptions = true;
        this.patientInfo.isEdit = false;
      }, 1000);
    } catch (e) {
      Logger.log('Ocurrió un error', e?.message);
      this.messageAlertService.createMessage('error', this.messageAlertService.getMessageError(e.message));
    } finally {
      await firstValueFrom((this.store.dispatch(new Loader(false))));
    }
  }

  /**
   * Setea los datos del paciente.
   * @param patient Paciente IPatient
   */
  async setPatient(patient: IPatient): Promise<void> {
    this.patient = cloneDeep(patient);
    if (!this.patient) return;

    /*if (this.patient.user && !this.patient.user.address) {
      this.patient.user.address = {
        city: null,
        country: null,
        cp: null,
        reference: null,
        state: null,
        street: null,
        suburb: null
      };
    }*/
    this.form = new FormGroup<ReactiveFormType<IPatientInformation>>({
      email: new FormControl<string>(this.patient.user?.email, [Validators.required]),
      // @ts-ignore
      birthday: new FormControl<string>(this.patient.user?.birthday ?? '', [Validators.required]),
      // @ts-ignore
      civilState: new FormControl<string>(this.patient.civilState, [Validators.required]),
      // @ts-ignore
      gender: new FormControl<string>(this.patient.user?.gender, [Validators.required]),
      country: new FormControl<string | null>(patient.user.address.country ?? '', []),
      suburb: new FormControl<string | null>(patient.user.address.suburb ?? '', []),
      state: new FormControl<string | null>(patient.user.address.state ?? '', []),
      street: new FormControl<string | null>(patient.user.address.street ?? '', []),
      city: new FormControl<string | null>(patient.user.address.city ?? '', []),
      cp: new FormControl<string | null>(patient.user.address.cp ?? '', []),
    });
  }


  ngOnDestroy(): void {
    this.patientSubscription.unsubscribe();
  }
}
